<template>
  <div class="jsplumb-flow-editor">
    <div class="flow-toolbar">
      <el-button type="primary" size="small" @click="confirmAddNode">添加步骤节点</el-button>
      <el-button type="info" size="small" @click="autoLayout">自动布局</el-button>
    </div>
    <div class="flow-canvas-container">
      <vue-flowchart
        ref="flowchart"
        :data="flowchartData"
        :node-templates="nodeTemplates"
        :options="flowchartOptions"
        @node:select="handleNodeSelect"
        @node:add="handleNodeAdd"
        @node:remove="handleNodeRemove"
        @link:add="handleLinkAdd"
        @link:remove="handleLinkRemove"
        @repaint="handleRepaint"
      ></vue-flowchart>
    </div>

    <!-- 节点编辑对话框 -->
    <el-dialog
      :visible.sync="nodeEditVisible"
      :title="`编辑步骤: ${editingNodeId}`"
      width="1000px"
      :close-on-click-modal="false"
      :append-to-body="true"
      custom-class="node-edit-dialog"
      @close="handleEditDialogClose"
      v-if="editingNodeId && editingStepData"
    >
      <el-tabs v-model="activeTab" type="card">
        <el-tab-pane label="基本信息" name="basic">
          <step-basic-editor :step-id="editingNodeId"></step-basic-editor>
        </el-tab-pane>
        <el-tab-pane label="选择项" v-if="!isActionStep" name="choices">
          <step-choices-editor :step-id="editingNodeId"></step-choices-editor>
        </el-tab-pane>
        <el-tab-pane label="条件" name="conditions">
          <step-conditions-editor :step-id="editingNodeId"></step-conditions-editor>
        </el-tab-pane>
        <el-tab-pane label="动作" name="actions">
          <step-actions-editor :step-id="editingNodeId"></step-actions-editor>
        </el-tab-pane>
      </el-tabs>
      <span slot="footer">
        <el-button type="primary" :size="size" @click="saveNodeEdit">保存</el-button>
        <el-button type="info" :size="size" @click="nodeEditVisible = false">取 消</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import VueFlowchart from "vue-flowchart";
import "vue-flowchart/dist/vue-flowchart.css";
import FlowNodeWidget from "./FlowNodeWidget.vue";
import { mapState, mapGetters } from "vuex";
import StepBasicEditor from "./StepBasicEditor.vue";
import StepChoicesEditor from "./StepChoicesEditor.vue";
import StepConditionsEditor from "./StepConditionsEditor.vue";
import StepActionsEditor from "./StepActionsEditor.vue";
import { Toolkit } from "@/util";

export default {
  name: "FlowEditor",
  components: {
    VueFlowchart,
    StepBasicEditor,
    StepChoicesEditor,
    StepConditionsEditor,
    StepActionsEditor
  },
  props: {
    steps: {
      type: Object,
      default: () => ({})
    },
    initialStepId: {
      type: String,
      default: ""
    }
  },

  computed: {
    ...mapState({
      size: (state) => state.btnSize
    }),
    ...mapGetters("problemFlowConfig", {
      storeSteps: "steps",
      stepIds: "stepIds"
    }),
    // props 中的 steps（避免与 computed 中的 steps 冲突）
    propsSteps() {
      return this.$props.steps || {};
    },
    // 判断当前编辑的步骤是否是动作步骤
    isActionStep() {
      if (this.editingStepData) {
        return this.editingStepData.actionStep === true;
      }
      if (this.editingNodeId) {
        const step = this.storeSteps[this.editingNodeId];
        return step?.actionStep === true;
      }
      return false;
    },
    // 将 storeSteps 转换为 vue-flowchart 需要的格式
    flowchartData() {
      const nodes = [];
      const links = [];
      let linkIdCounter = 0;

      Object.keys(this.storeSteps).forEach((stepId) => {
        const step = this.storeSteps[stepId];
        const { flowChartConfig = {} } = step;
        const node = {
          id: stepId,
          type: "default",
          data: {
            stepName: step.stepName || "",
            problemStatus: step.problemStatus || null,
            isInitial: stepId === this.initialStepId,
            isSelected: this.selectedNodeId === stepId,
            ...step
          },
          x: flowChartConfig.x || 100,
          y: flowChartConfig.y || 100
        };
        nodes.push(node);

        // 从条件中提取连接
        if (step.conditions && Array.isArray(step.conditions)) {
          step.conditions.forEach((condition) => {
            if (condition.nextStepId && this.storeSteps[condition.nextStepId]) {
              links.push({
                id: `link-${linkIdCounter++}`,
                source: stepId,
                sourcePort: "out",
                target: condition.nextStepId,
                targetPort: "in",
                type: "condition"
              });
            }
          });
        }

        // 从默认下一步提取连接
        if (step.defaultNextStepId && this.storeSteps[step.defaultNextStepId]) {
          links.push({
            id: `link-${linkIdCounter++}`,
            source: stepId,
            sourcePort: "out",
            target: step.defaultNextStepId,
            targetPort: "in",
            type: "default"
          });
        }
      });

      // 直接返回新对象，让 vue-flowchart 自己处理变化检测
      return { nodes, links };
    }
  },
  watch: {
    // 监听 props 中的 steps 变化
    propsSteps: {
      handler(newSteps) {
        // 如果 props 中的 steps 变化，同步到 Vuex
        if (newSteps && Object.keys(newSteps).length > 0) {
          const currentStoreSteps = this.storeSteps;
          // 只有当 props 数据与 store 数据不同时才更新
          // 使用浅比较避免深度比较导致的性能问题
          const newKeys = Object.keys(newSteps);
          const currentKeys = Object.keys(currentStoreSteps);
          if (newKeys.length !== currentKeys.length) {
            this.loadSteps(newSteps);
            return;
          }
          // 检查是否有新增或删除的步骤
          const hasDiff =
            newKeys.some((key) => !currentStoreSteps[key]) ||
            currentKeys.some((key) => !newSteps[key]);
          if (hasDiff) {
            this.loadSteps(newSteps);
            return;
          }
        } else if (
          newSteps &&
          Object.keys(newSteps).length === 0 &&
          Object.keys(this.storeSteps).length > 0
        ) {
          // props 为空但 store 有数据，清空 store
          this.$store.dispatch("problemFlowConfig/setSteps", {});
        }
      },
      immediate: true,
      deep: false // 改为浅监听，避免深度监听导致的性能问题
    },
    initialStepId: {
      handler() {
        // 不需要 forceUpdate，computed 会自动更新
      }
    }
  },
  data() {
    return {
      selectedNodeId: null,
      nodeEditVisible: false,
      editingNodeId: null,
      editingStepData: null, // 临时编辑状态，保存时才会提交到 store
      activeTab: "basic",
      nodeWidth: 200,
      nodeHeight: 120,
      addNodeForm: {
        stepId: ""
      },
      addNodeRules: {
        stepId: [
          { required: true, message: "请输入步骤ID", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              if (this.storeSteps[value]) {
                callback(new Error("步骤ID已存在"));
              } else {
                callback();
              }
            },
            trigger: "blur"
          }
        ]
      },
      nodeTemplates: [
        [
          "default",
          FlowNodeWidget,
          {
            inPorts: ["in"],
            outPorts: ["out"],
            onEdit: (nodeId) => {
              this.editNode(nodeId);
            }
          }
        ]
      ],
      flowchartOptions: {
        onNodeRemove: () => {
          return Promise.resolve(true);
        },
        onEdgeRemove: () => {
          return Promise.resolve(true);
        },
        onEdgeUpdate: () => {
          return Promise.resolve(true);
        }
      },
      engine: null, // 用于存储 engine 实例
      repaintTimer: null, // 防抖定时器
      updateTimer: null, // update 事件防抖定时器
      positionWatcherCleanup: null, // 位置监听器清理函数
      isUpdatingPosition: false, // 是否正在更新位置，防止循环更新
      cachedFlowchartData: null, // 缓存的流程图数据
      cachedStoreStepsKeys: "" // 缓存的步骤键值
    };
  },
  provide() {
    const self = this;
    return {
      getEngine: () => {
        // 从 vue-flowchart 组件实例中获取 engine
        return self.engine || (self.$refs.flowchart && self.$refs.flowchart.engine) || null;
      },
      // 提供编辑状态和更新方法给子组件
      getEditingStepData: () => {
        return self.editingStepData;
      },
      updateEditingStepField: (field, value) => {
        if (self.editingStepData) {
          self.$set(self.editingStepData, field, value);
        }
      },
      updateEditingStepActions: (actionType, actions) => {
        if (self.editingStepData) {
          self.$set(self.editingStepData, actionType, actions);
        }
      }
    };
  },
  mounted() {
    // 监听节点编辑事件
    this.$root.$on("flow-node-edit", this.editNode);
    // 监听节点删除事件
    this.$root.$on("flow-node-remove", this.removeNode);
    // 初始化时，如果 props 有数据，加载到 Vuex
    if (this.propsSteps && Object.keys(this.propsSteps).length > 0) {
      this.loadSteps(this.propsSteps);
    } else {
      // 如果没有数据，初始化空状态
      this.$store.dispatch("problemFlowConfig/setSteps", {});
    }
    // 等待 vue-flowchart 组件挂载后获取 engine
    this.$nextTick(() => {
      if (this.$refs.flowchart && this.$refs.flowchart.engine) {
        this.engine = this.$refs.flowchart.engine;
        // 监听节点位置变化
        this.setupNodePositionWatcher();
      }
    });
  },

  beforeDestroy() {
    // 移除事件监听
    this.$root.$off("flow-node-edit", this.editNode);
    this.$root.$off("flow-node-remove", this.removeNode);
    // 清理位置监听器
    if (this.positionWatcherCleanup) {
      this.positionWatcherCleanup();
    }
    // 清理定时器
    if (this.repaintTimer) {
      clearTimeout(this.repaintTimer);
    }
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
  },

  methods: {
    /**
     * 加载步骤数据
     */
    loadSteps(steps) {
      if (!steps || Object.keys(steps).length === 0) {
        this.$store.dispatch("problemFlowConfig/setSteps", {});
        return;
      }

      // 加载节点到 Vuex，确保每个步骤都有 flowChartConfig
      const stepsWithPosition = {};
      Object.keys(steps).forEach((stepId) => {
        const step = steps[stepId];
        const flowChartConfig = step.flowChartConfig || {};

        // 如果 flowChartConfig 中没有坐标，设置默认值
        if (!flowChartConfig.x && !flowChartConfig.y) {
          flowChartConfig.x = Math.random() * 500 + 100;
          flowChartConfig.y = Math.random() * 400 + 100;
        }

        // 将空字符串字段转换为 null
        const cleanedStep = { ...step };
        if (cleanedStep.problemStatus === "") {
          cleanedStep.problemStatus = null;
        }
        if (cleanedStep.replyTemplate === "") {
          cleanedStep.replyTemplate = null;
        }
        if (cleanedStep.defaultNextStepId === "") {
          cleanedStep.defaultNextStepId = null;
        }

        stepsWithPosition[stepId] = {
          ...cleanedStep,
          flowChartConfig
        };
      });

      this.$store.dispatch("problemFlowConfig/setSteps", stepsWithPosition);
    },

    /**
     * 确认添加节点
     */
    confirmAddNode() {
      const stepId = Toolkit.uuid();
      const container =
        this.$refs.flowchart?.$el || document.querySelector(".flow-canvas-container");
      if (!container) {
        this.$message.error("画布容器未找到");
        return;
      }

      // 计算新节点的位置，避免重叠
      const existingNodes = Object.values(this.storeSteps).filter((node) => {
        const flowChartConfig = node.flowChartConfig || {};
        return (
          flowChartConfig.x !== undefined &&
          flowChartConfig.y !== undefined &&
          flowChartConfig.x !== null &&
          flowChartConfig.y !== null
        );
      });

      // 如果没有现有节点，放在中心位置
      let newX = 100;
      let newY = 100;

      if (existingNodes.length > 0) {
        // 找到最右侧的节点位置
        const maxX = Math.max(
          ...existingNodes.map((node) => {
            const flowChartConfig = node.flowChartConfig || {};
            return (flowChartConfig.x || 0) + this.nodeWidth + 50;
          })
        );
        const maxY = Math.max(
          ...existingNodes.map((node) => {
            const flowChartConfig = node.flowChartConfig || {};
            return flowChartConfig.y || 0;
          })
        );

        // 新节点放在最右侧节点的右侧
        newX = maxX;
        newY = maxY;

        // 如果超出容器宽度，换到下一行
        const containerWidth = container.clientWidth || 800;
        if (newX + this.nodeWidth > containerWidth - 20) {
          newX = 100;
          newY = maxY + this.nodeHeight + 50;
        }
      } else {
        // 第一个节点放在中心偏左
        const containerWidth = container.clientWidth || 800;
        const containerHeight = container.clientHeight || 600;
        newX = Math.max(20, (containerWidth - this.nodeWidth) / 2 - 100);
        newY = Math.max(20, (containerHeight - this.nodeHeight) / 2);
      }

      // 确保位置在有效范围内
      const containerWidth = container.clientWidth || 800;
      const containerHeight = container.clientHeight || 600;
      newX = Math.max(20, Math.min(newX, containerWidth - this.nodeWidth - 20));
      newY = Math.max(20, Math.min(newY, containerHeight - this.nodeHeight - 20));

      const stepData = {
        stepId: stepId,
        stepName: "",
        replyTemplate: null,
        problemStatus: null,
        nextAllowedComTasks: [],
        nextAllowedSpecTasks: null,
        choices: [],
        conditions: [],
        actions: [],
        actionStep: false,
        flowChartConfig: {
          x: newX,
          y: newY
        }
      };

      this.$store.dispatch("problemFlowConfig/addStep", { stepId, stepData });

      // 延迟触发 update 事件，避免频繁触发
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      this.updateTimer = setTimeout(() => {
        this.$emit("update", this.getStepsData());
      }, 100);
    },

    /**
     * 删除节点
     */
    removeNode(nodeId) {
      this.$confirm("确认删除该步骤吗?", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      })
        .then(() => {
          // 更新步骤中的条件
          Object.keys(this.storeSteps).forEach((id) => {
            const node = this.storeSteps[id];
            if (node.conditions) {
              const conditions = node.conditions.filter((c) => c.nextStepId !== nodeId);
              if (conditions.length !== node.conditions.length) {
                this.$store.dispatch("problemFlowConfig/updateStepField", {
                  stepId: id,
                  field: "conditions",
                  value: conditions
                });
              }
            }
            if (node.defaultNextStepId === nodeId) {
              this.$store.dispatch("problemFlowConfig/updateStepField", {
                stepId: id,
                field: "defaultNextStepId",
                value: ""
              });
            }
          });

          // 删除节点
          this.$store.dispatch("problemFlowConfig/removeStep", nodeId);

          // 延迟触发 update 事件，避免频繁触发
          if (this._updateTimer) {
            clearTimeout(this._updateTimer);
          }
          this._updateTimer = setTimeout(() => {
            this.$emit("update", this.getStepsData());
          }, 100);
        })
        .catch(() => {
          // 用户取消
        });
    },

    /**
     * 编辑节点
     */
    editNode(nodeId) {
      this.selectedNodeId = nodeId;
      this.editingNodeId = nodeId;
      this.$store.dispatch("problemFlowConfig/setEditingStep", nodeId);
      // 深拷贝当前步骤数据到临时编辑状态
      const currentStep = this.storeSteps[nodeId] || {};
      this.editingStepData = JSON.parse(
        JSON.stringify({
          ...currentStep,
          stepId: nodeId // 确保包含 stepId
        })
      );
      this.nodeEditVisible = true;
      this.activeTab = "basic";
    },

    /**
     * 保存节点编辑
     */
    saveNodeEdit() {
      if (this.editingNodeId && this.editingStepData) {
        // 将临时编辑状态提交到 store
        this.$store.dispatch("problemFlowConfig/updateStep", {
          stepId: this.editingNodeId,
          stepData: this.editingStepData
        });
      }
      this.editingStepData = null;
      this.nodeEditVisible = false;
      // 延迟触发 update 事件，避免频繁触发
      if (this._updateTimer) {
        clearTimeout(this._updateTimer);
      }
      this._updateTimer = setTimeout(() => {
        this.$emit("update", this.getStepsData());
      }, 100);
    },

    /**
     * 自动布局
     */
    autoLayout() {
      const nodeIds = Object.keys(this.storeSteps);
      if (nodeIds.length === 0) {
        this.$message.warning("没有节点需要布局");
        return;
      }

      // 计算节点层次
      const levels = this.calculateLevels();
      const levelHeight = 180;
      const nodeSpacing = 250;

      const container =
        this.$refs.flowchart?.$el || document.querySelector(".flow-canvas-container");
      if (!container) return;

      const containerWidth = container.clientWidth || 800;

      Object.keys(levels).forEach((level) => {
        const nodesInLevel = levels[level];
        const startX = (containerWidth - (nodesInLevel.length - 1) * nodeSpacing) / 2;
        nodesInLevel.forEach((nodeId, index) => {
          this.$store.dispatch("problemFlowConfig/updateStepField", {
            stepId: nodeId,
            field: "flowChartConfig.x",
            value: Math.max(20, startX + index * nodeSpacing)
          });
          this.$store.dispatch("problemFlowConfig/updateStepField", {
            stepId: nodeId,
            field: "flowChartConfig.y",
            value: parseInt(level) * levelHeight + 100
          });
        });
      });

      this.$message.success("自动布局完成");
      // 延迟触发 update 事件，避免频繁触发
      if (this._updateTimer) {
        clearTimeout(this._updateTimer);
      }
      this._updateTimer = setTimeout(() => {
        this.$emit("update", this.getStepsData());
      }, 200);
    },

    /**
     * 计算节点层次
     */
    calculateLevels() {
      const levels = {};
      const visited = new Set();
      const queue = [];

      // 从初始步骤开始
      if (this.initialStepId && this.storeSteps[this.initialStepId]) {
        queue.push({ id: this.initialStepId, level: 0 });
      } else {
        // 如果没有初始步骤，从第一个节点开始
        const firstNodeId = Object.keys(this.storeSteps)[0];
        if (firstNodeId) {
          queue.push({ id: firstNodeId, level: 0 });
        }
      }

      while (queue.length > 0) {
        const { id, level } = queue.shift();
        if (visited.has(id)) continue;
        visited.add(id);

        if (!levels[level]) {
          levels[level] = [];
        }
        levels[level].push(id);

        // 找到所有连接的节点（从 conditions 和 defaultNextStepId）
        const node = this.storeSteps[id];
        const nextNodes = new Set();

        // 从条件中提取下一步
        if (node.conditions && Array.isArray(node.conditions)) {
          node.conditions.forEach((condition) => {
            if (condition.nextStepId && this.storeSteps[condition.nextStepId]) {
              nextNodes.add(condition.nextStepId);
            }
          });
        }

        // 从默认下一步提取
        if (node.defaultNextStepId && this.storeSteps[node.defaultNextStepId]) {
          nextNodes.add(node.defaultNextStepId);
        }

        // 将下一步节点加入队列
        nextNodes.forEach((nextId) => {
          if (!visited.has(nextId)) {
            queue.push({ id: nextId, level: level + 1 });
          }
        });
      }

      // 处理未访问的节点（孤立节点）
      Object.keys(this.storeSteps).forEach((nodeId) => {
        if (!visited.has(nodeId)) {
          const maxLevel = Math.max(...Object.keys(levels).map(Number), -1);
          if (!levels[maxLevel + 1]) {
            levels[maxLevel + 1] = [];
          }
          levels[maxLevel + 1].push(nodeId);
        }
      });

      return levels;
    },

    /**
     * 获取步骤数据
     */
    getStepsData() {
      return this.storeSteps;
    },

    /**
     * 处理节点选择
     */
    handleNodeSelect(data) {
      if (data && data.node) {
        this.selectedNodeId = data.node.id;
        // 双击节点时编辑
        if (data.event && data.event.type === "dblclick") {
          this.editNode(data.node.id);
        }
      }
    },

    /**
     * 处理节点添加
     */
    handleNodeAdd() {
      // 节点添加事件
    },

    /**
     * 处理节点删除
     */
    handleNodeRemove(data) {
      if (data && data.node) {
        this.removeNode(data.node.id);
      }
    },

    /**
     * 处理连接添加
     */
    handleLinkAdd() {
      // 连接添加事件
    },

    /**
     * 处理连接删除
     */
    handleLinkRemove() {
      // 连接删除事件
    },

    /**
     * 处理重绘事件 - 检查节点位置变化并更新到 store
     */
    handleRepaint() {
      if (!this.engine || !this.engine.state || !this.engine.state.nodes) {
        return;
      }

      // 使用防抖来避免频繁更新，增加防抖时间
      if (this.repaintTimer) {
        clearTimeout(this.repaintTimer);
      }

      this.repaintTimer = setTimeout(() => {
        // 检查所有节点的位置是否变化
        const engineNodes = this.engine.state.nodes;
        const positionUpdates = [];

        Object.keys(engineNodes).forEach((nodeId) => {
          const engineNode = engineNodes[nodeId];
          const storeStep = this.storeSteps[nodeId];

          if (storeStep && engineNode) {
            const newX = Math.round(engineNode.x);
            const newY = Math.round(engineNode.y);
            const flowChartConfig = storeStep.flowChartConfig || {};
            const oldX = flowChartConfig.x;
            const oldY = flowChartConfig.y;

            // 如果位置发生变化（使用四舍五入避免浮点数精度问题），记录更新
            if (newX !== oldX || newY !== oldY) {
              positionUpdates.push({ nodeId, x: newX, y: newY });
            }
          }
        });

        // 批量更新位置，避免多次触发更新
        if (positionUpdates.length > 0) {
          // 使用标志位防止循环更新
          this.isUpdatingPosition = true;
          positionUpdates.forEach(({ nodeId, x, y }) => {
            this.$store.dispatch("problemFlowConfig/updateStepField", {
              stepId: nodeId,
              field: "flowChartConfig.x",
              value: x
            });
            this.$store.dispatch("problemFlowConfig/updateStepField", {
              stepId: nodeId,
              field: "flowChartConfig.y",
              value: y
            });
          });
          this.isUpdatingPosition = false;

          // 延迟触发 update 事件，避免频繁触发
          if (this.updateTimer) {
            clearTimeout(this.updateTimer);
          }
          this.updateTimer = setTimeout(() => {
            this.$emit("update", this.getStepsData());
          }, 300);
        }
      }, 300); // 增加防抖时间到 300ms
    },

    /**
     * 设置节点位置监听器
     */
    setupNodePositionWatcher() {
      // 监听鼠标抬起事件，在节点拖动结束时保存位置
      if (this.engine && this.engine.state && this.engine.state.canvas) {
        const canvas = this.engine.state.canvas;
        const handleMouseUp = () => {
          this.handleRepaint();
        };

        canvas.addEventListener("mouseup", handleMouseUp);
        this.positionWatcherCleanup = () => {
          if (canvas) {
            canvas.removeEventListener("mouseup", handleMouseUp);
          }
        };
      }
    },

    /**
     * 处理编辑对话框关闭
     */
    handleEditDialogClose() {
      // 关闭对话框时清空临时编辑状态（不保存）
      this.editingStepData = null;
    }
  }
};
</script>

<style lang="less" scoped>
.jsplumb-flow-editor {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .flow-toolbar {
    padding: 10px;
    background: #f5f7fa;
    border-bottom: 1px solid #dcdfe6;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .flow-canvas-container {
    flex: 1;
    position: relative;
    overflow: auto;
    background: #fafafa;
    background-image: linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
    background-size: 20px 20px;
    min-height: 500px;
  }
}

// vue-flowchart 样式覆盖
::v-deep .vue-flowchart {
  width: 100%;
  height: 100%;
}

/deep/ .el-dialog.node-edit-dialog {
  margin-top: 10px !important;
}
</style>
